import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail } from "lucide-react";
import { Wordmark, Asterisk } from "@/components/Motifs";
import { savedInvite } from "@/lib/api";
import { useGuest, celebrationsLabel } from "@/lib/GuestContext";

const tid = (l) => l.toLowerCase().replace(/[^a-z]/g, "-");
const link = (to, label, id) => ({ to, label, id: id || tid(label) });

export function useNavLinks() {
  const { guest } = useGuest();
  const n = guest?.events?.length || 0;
  return [
    link("/", "Home"),
    link("/story", "Our Story"),
    ...(n ? [link("/celebrations", celebrationsLabel(n), "celebrations"), link("/dress-code", "Dress Code")] : []),
    link("/faqs", "FAQs"),
    link("/gallery", "Gallery"),
  ];
}
export const inviteHref = () => (savedInvite() ? `/invite/${savedInvite()}` : "/#invitation");

export default function Nav() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const links = useNavLinks();
  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  return (
    <header data-testid="site-nav" className="sticky top-0 z-40 bg-[#FFF9F0] lg:bg-[#FFF9F0]/85 lg:backdrop-blur-md border-b-2 border-[#252122]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-[68px] flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" aria-label="NiGi home"><Wordmark className="text-3xl" /></Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-${l.id}`}
              className={({ isActive }) => `font-ui text-[13px] font-bold flex items-center gap-1.5 link-sweep ${isActive ? "text-[#A62D58]" : "text-[#252122] hover:text-[#A62D58]"}`}>
              {({ isActive }) => (<>{isActive && <Asterisk size={11} />}{l.label}</>)}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to={inviteHref()} data-testid="nav-invite-cta" className="btn-pop bg-[#FF6657] text-[#252122] rounded-full px-4 md:px-5 py-2 kicker inline-flex items-center gap-2">
            <Mail size={14} /><span className="hidden sm:inline">My Invitation</span><span className="sm:hidden">Invite</span>
          </Link>
          <button className="lg:hidden w-11 h-11 rounded-full border-2 border-[#252122] flex items-center justify-center" data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-x-0 top-[68px] bottom-0 bg-[#252122] text-[#FFF9F0] overflow-y-auto page-enter" data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col">
            {links.map((l, i) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`m-nav-${l.id}`}
                className={({ isActive }) => `flex items-baseline gap-4 py-3 border-b border-[#FFF9F0]/10 ${isActive ? "text-[#F7B733]" : ""}`}>
                <span className="kicker text-[#FF6657] w-6">0{i + 1}</span>
                <span className="font-display italic text-4xl">{l.label}</span>
              </NavLink>
            ))}
            <Link to={inviteHref()} data-testid="m-nav-invite" className="mt-8 btn-pop bg-[#FF6657] text-[#252122] rounded-full py-4 kicker text-center" style={{ borderColor: "#FFF9F0", boxShadow: "4px 4px 0 #FFF9F0" }}>
              Open my invitation
            </Link>
            <div className="mt-10 font-hand text-2xl text-[#F7B733]">#MaamlaNiGiHai</div>
            <div className="kicker mt-2 opacity-60">03.12.2026 · Mumbai</div>
          </div>
        </div>
      )}
    </header>
  );
}
